// marcTransform is stored in canvas-virtual px (CANVAS_WIDTH × CANVAS_HEIGHT
// from constants.js). Both the drawing side (OnionMarc) and the reference
// side (ReferencePhoto) use it to position Marc. Storing in virtual px
// means the stored values are display-size-invariant — the same numbers
// position Marc identically at any viewport size. The two surfaces render
// it differently:
//
//   Drawing side: OnionMarc sits inside FitContainer, which applies a
//     transform: scale(S) to its virtually-sized Inner. marcTransformToCss
//     is called with mult=1 (default) — the outer scale handles the
//     virtual→display mapping, and tx/ty stay as literal virtual px.
//
//   Reference side: the Frame is measured directly via useSize and sized
//     in display px. marcTransformToCss is called with mult=displayScale
//     so the tx/ty shifts come out in display px. `scale` doesn't need
//     mult here — the <img> is already sized at display dims, and
//     `scale` is just "Marc's zoom on top" in display units either way.
//
// marcTransform shape: { tx, ty, scale, rot }
//   tx, ty:  canvas-virtual px translation from frame center
//   scale:   multiplicative, default 1 (Marc's zoom factor)
//   rot:     degrees, applied after translate, around frame center

// Forward transform → CSS string for the Marc <img>. Applied to an element
// positioned at left:50%/top:50% with an initial translate(-50%, -50%)
// centering hack so the image's center sits on the frame's center before
// marcTransform kicks in.
//
// `mult` scales the virtual tx/ty translation into whatever unit the <img>
// lives in. See the module header for when to pass 1 vs displayScale.
export function marcTransformToCss(transform, mult = 1) {
  const { tx = 0, ty = 0, scale = 1, rot = 0 } = transform ?? {};
  return `translate(-50%, -50%) translate(${tx * mult}px, ${ty * mult}px) rotate(${rot}deg) scale(${scale})`;
}

// Egg version. Same translate-then-rotate-then-scale pattern as marc, plus
// a horizontal scaleX squish (the head-shape lever). Lives in canvas-
// virtual px exclusively — the egg only ever renders inside the
// FitContainer-scaled drawing surface, so no display-scale `mult` is
// needed. Defaults match DEFAULT_EGG_TRANSFORM so a missing field reads
// as the canonical baseline.
export function eggTransformToCss(transform) {
  const {
    tx = 0,
    ty = 0,
    scale = 1,
    scaleX = 1,
    rotation = 0,
  } = transform ?? {};
  return `translate(-50%, -50%) translate(${tx}px, ${ty}px) rotate(${rotation}deg) scale(${scale}) scaleX(${scaleX})`;
}

// Inverse of marcTransform: given a point (px, py) in canvas-virtual px
// (i.e. the caller has already divided display coords by the display
// scale), return the point's location in the image's LAYOUT box
// (pre-object-fit). Used by the reference photo eyedropper to map a
// viewport click back to the source image's pixel grid.
export function invertMarcTransform(px, py, transform, frameW, frameH) {
  const { tx = 0, ty = 0, scale = 1, rot = 0 } = transform ?? {};
  const cx = frameW / 2;
  const cy = frameH / 2;
  const dx = px - (cx + tx);
  const dy = py - (cy + ty);
  const theta = (-rot * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const s = scale === 0 ? 1 : scale;
  const rx = (dx * cos - dy * sin) / s;
  const ry = (dx * sin + dy * cos) / s;
  return { x: rx + cx, y: ry + cy };
}
